import React, { useState, useEffect } from "react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { AgGridReact } from "ag-grid-react";
import moment from "moment/moment";
import "../pagination/pagination.css";
import Pagination from "../pagination/pagination";
import Select from "react-select";
import { RiRefreshLine } from "react-icons/ri";
import { FaFileCircleExclamation } from "react-icons/fa6";
import LeaseApiService from "../../services/apiService";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ClipLoader } from 'react-spinners';
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

const UnitVacantReport = () => {
    const apiService = new LeaseApiService();
    const [rowData, setRowData] = useState([]);
    const [exportData, setExportData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loader, setLoader] = useState(false);
    const [pageSize, setPageSize] = useState(20);
    const [pagination, setPagination] = useState();
    const [FilterTab, setFilterTab] = useState(true);
    const [propertyList, setPropertyList] = useState([]);
    const [unitList, setUnitList] = useState([]);

    const [formData, setFormData] = useState({
        property: "",
        unitNo: "",
        from: "",
        to: "",
    })

    const formatDate = (date) => {
        return moment(date).format("DD-MMM-YYYY");
    };

    useEffect(() => {
        fetchDropdownData()
        const today = new Date();
        const monthStart = moment(today).startOf("month").toDate();
        setFormData((prev) => ({ ...prev, from: formatDate(monthStart), to: formatDate(today) }));
    }, []);

    const fetchDropdownData = async () => {
        try {
            const params = new URLSearchParams();
            params.append('dropdownTypes', 'LRAgreeSearchProperties');
            const res = await apiService.getApiParamater('LEASE_REPORTS_MULTIDROP', `?${params.toString()}`);
            setPropertyList(res?.data?.dropdowns?.LRAgreeSearchProperties || []);
        } catch (error) {
            console.error('Error fetching dropdown data:', error);
        }
    }
    const getItems = async (newPageNo = pagination?.currentPage) => {
        try {
            setLoader(true)
            const today = new Date();
            let todate = formatDate(today)
            const params = new URLSearchParams();
            params.append("PageNo", newPageNo || 1);
            params.append("PageSize", pageSize || 20);
            params.append("PropertyName", formData?.property?.label || "ALL");
            params.append("UnitNo", formData?.unitNo?.label || "ALL");
            params.append("FromDate", formData?.from || todate);
            params.append("ToDate", formData?.to || todate);
            const res = await apiService.getApiParamater("LEASE_VACANT_UNITS_LIST", `?${params.toString()}`);
            if (res?.data?.result) {
                setRowData(res?.data?.result || []);
                setPagination(res.data.paginationData);
                getAllItems(res.data.paginationData)
                setLoading(false);
                setLoader(false)
            } else {
                setRowData(res?.data?.result || []);
                toast.error(res?.data?.message)
                setLoader(false)
                setLoading(false);
            }
        } catch (error) {
            console.error("Error fetching items:", error);
            setLoader(false)
            setLoading(false);
        }
    }
    const getAllItems = async (total) => {
        // Fetch every page individually (not one oversized page - some backends
        // cap PageSize and silently return just the first page) and stitch them
        // together. Fetch sequentially and stop once we have totalRecords worth
        // of rows or a page comes back empty, then hard-cap the stitched array
        // at totalRecords - some backends return duplicate/wrapped rows instead
        // of an empty page once PageNo runs past the real last page, which was
        // inflating the export beyond what the on-screen search actually found.
        try {
            setLoader(true)
            const today = new Date();
            let todate = formatDate(today)
            const totalRecords = total?.totalRecords || 0;
            if (!totalRecords) {
                setExportData([]);
                setLoading(false);
                setLoader(false)
                return;
            }
            const batchSize = pageSize || 20;
            const buildParams = (pageNo) => {
                const params = new URLSearchParams();
                params.append("PageNo", pageNo);
                params.append("PageSize", batchSize);
                params.append("PropertyName", formData?.property?.label || "ALL");
                params.append("UnitNo", formData?.unitNo?.label || "ALL");
                params.append("FromDate", formData?.from || todate);
                params.append("ToDate", formData?.to || todate);
                return params;
            };
            let allResults = [];
            let pageNo = 1;
            const maxPages = Math.ceil(totalRecords / batchSize) + 1; // small safety margin
            while (allResults.length < totalRecords && pageNo <= maxPages) {
                const res = await apiService.getApiParamater("LEASE_VACANT_UNITS_LIST", `?${buildParams(pageNo).toString()}`);
                const pageResults = res?.data?.result || [];
                if (!pageResults.length) break;
                allResults = allResults.concat(pageResults);
                pageNo++;
            }
            setExportData(allResults.slice(0, totalRecords));
            setLoading(false);
            setLoader(false)
        } catch (error) {
            console.error("Error fetching items:", error);
            setLoading(false);
            setLoader(false)
        }
    }
    const normalizeValue = (value) => {
        if (value === null || value === undefined) return "";
        if (value === 0 || value === "0") return "";

        if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) {
            return moment(value).format("DD-MMM-YYYY");
        }

        return value;
    };
    const dateFormatter = (params) => {
        const value = params.value;
        if (!value) return "";
        return moment(value).format("DD-MMM-YYYY");
    };
    const numberFormatter = (params) => {
        const value = params.value;
        if (value === null || value === undefined || value === "") return "";
        return Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };
    const percentFormatter = (params) => {
        const value = params.value;
        if (value === null || value === undefined || value === "") return "";
        return `${Number(value).toFixed(2)}%`;
    };
    const columnDefs = [
        { headerName: "Property Name", field: "property_Name", filter: true, floatingFilter: true, suppressMovable: true, width: 200, pinned: "left" },
        { headerName: "Unit No", field: "unit_No", filter: true, floatingFilter: true, suppressMovable: true, width: 120, pinned: "left" },
        { headerName: "From Date", field: "fromDate", filter: true, floatingFilter: true, suppressMovable: true, width: 130, valueFormatter: dateFormatter },
        { headerName: "To Date", field: "toDate", filter: true, floatingFilter: true, suppressMovable: true, width: 130, valueFormatter: dateFormatter },
        { headerName: "Total Days", field: "totalDays", filter: true, floatingFilter: true, suppressMovable: true, width: 120 },
        { headerName: "Occupied Days", field: "occupiedDays", filter: true, floatingFilter: true, suppressMovable: true, width: 130 },
        { headerName: "Vacant Days", field: "vacantDays", filter: true, floatingFilter: true, suppressMovable: true, width: 120 },
        { headerName: "Revenue Loss", field: "revenueLoss", filter: true, floatingFilter: true, suppressMovable: true, width: 140, valueFormatter: numberFormatter },
        { headerName: "Vacant %", field: "vacantPercent", filter: true, floatingFilter: true, suppressMovable: true, width: 110, valueFormatter: percentFormatter },
        { headerName: "Last Lease Expiry", field: "lastLeaseExpiry", filter: true, floatingFilter: true, suppressMovable: true, width: 150, valueFormatter: dateFormatter },
        { headerName: "Vacant Status", field: "vacancyStatus", filter: true, floatingFilter: true, suppressMovable: true, width: 130 },
    ]
    const handleClearFilters = () => {
        const today = new Date();
        const monthStart = moment(today).startOf("month").toDate();
        setFormData({
            property: "",
            unitNo: "",
            from: formatDate(monthStart),
            to: formatDate(today),
        });
        setUnitList([]);
    }
    const handleDropDownChange = async ({ name, value }) => {
        if (name === "property") {
            setFormData(prev => ({ ...prev, unitNo: null }));
            const propertyValue = value?.value === 'All' ? null : value?.value;
            const params = new URLSearchParams();
            params.append('DropdownFor', 'LRAgreehUnitNosByPropertyId');
            params.append('CascadValue', propertyValue?.value);
            const res = await apiService.getApiParamater('LEASE_REPORTS_CASCAD', `?${params.toString()}`);
            setUnitList(res?.data?.items || []);
            setFormData(prev => ({ ...prev, [name]: value }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    }
    const handleDateChange = async (input) => {
        const name = input?.target?.name || input.name;
        const value = input?.target?.value || input.value;

        const updatedValue = typeof value === 'string' ? value : formatDate(value);
        let updatedDates = { ...formData, [name]: updatedValue };
        setFormData(updatedDates);
    }
    const reportColumns = [
        { header: "Property Name", dataKey: "property_Name" },
        { header: "Unit No", dataKey: "unit_No" },
        { header: "From Date", dataKey: "fromDate" },
        { header: "To Date", dataKey: "toDate" },
        { header: "Total Days", dataKey: "totalDays" },
        { header: "Occupied Days", dataKey: "occupiedDays" },
        { header: "Vacant Days", dataKey: "vacantDays" },
        { header: "Revenue Loss", dataKey: "revenueLoss" },
        { header: "Vacant %", dataKey: "vacantPercent" },
        { header: "Last Lease Expiry", dataKey: "lastLeaseExpiry" },
        { header: "Vacant Status", dataKey: "vacancyStatus" },
    ];
    const exportPDF = () => {
        if (!exportData?.length) return;

        const doc = new jsPDF("landscape", "pt", "A4");

        doc.setFillColor(110, 81, 153); // #6E5199
        doc.rect(0, 0, doc.internal.pageSize.width, 50, "F");

        doc.setTextColor(255);
        doc.setFontSize(16);
        doc.text("Unit Vacant Report", 40, 32);

        doc.setFontSize(9);
        doc.text(
            `Generated on: ${moment(formData?.to).format("DD-MMM-YYYY")}`,
            doc.internal.pageSize.width - 160,
            32
        );

        doc.setTextColor(0);

        doc.autoTable({
            startY: 60,
            head: [reportColumns.map(col => col.header)],
            body: exportData.map(row =>
                reportColumns.map(col => normalizeValue(row[col.dataKey]))
            ),
            styles: {
                fontSize: 8,
                cellPadding: 5,
                valign: "middle",
            },
            headStyles: {
                fillColor: [110, 81, 153],
                textColor: 255,
                fontStyle: "bold",
                halign: "center",
            },
            bodyStyles: {
                halign: "center",
            },
            alternateRowStyles: {
                fillColor: [240, 237, 245],
            },
            didDrawPage: data => {
                doc.setFontSize(8);
                doc.text(
                    `Page ${data.pageNumber}`,
                    doc.internal.pageSize.width / 2,
                    doc.internal.pageSize.height - 10,
                    { align: "center" }
                );
            },
        });
        doc.save("Unit_Vacant_Report.pdf");
    };
    const exportExcel = () => {
        if (!exportData?.length) return;
        const excelData = exportData.map(item => {
            const row = {};
            reportColumns.forEach(col => {
                row[col.header] = normalizeValue(item[col.dataKey]);
            });
            return row;
        });

        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Unit Vacant Report");
        XLSX.writeFile(workbook, "Unit_Vacant_Report.xlsx");
    };
    return (
        <div className="p-3">
            <div className="cost-center-section">
                <div
                    className="d-flex mt-3 cursor-pointer slide-header"
                    onClick={() => setFilterTab(!FilterTab)}
                >
                    <div className="h6 pt-1">Search Parameters</div>
                </div>
                <div className={`slide-content ${FilterTab ? "open" : "closed"}`}>
                    <div className={`border-box2 p-3 ${FilterTab ? "slide-in" : "slide-out"}`}>
                        <div className="form-group grid-contain ">
                            <div className="d-flex">
                                <label htmlFor="validationCustom18" className="form-label">Property</label>
                                <Select
                                    className="p-0 w-100"
                                    theme={(theme) => ({
                                        ...theme,
                                        borderRadius: 5,
                                        padding: 9,
                                    })}
                                    menuPortalTarget={document.body}
                                    styles={{
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                        option: (provided, state) => ({
                                            ...provided,
                                            backgroundColor: state.isFocused ? "#F5F5F5 " : null,
                                            color: state.isFocused ? "#000 " : "#000",
                                            ":hover": {
                                                backgroundColor: "#F5F5F5",
                                                color: "#000 ",
                                            },
                                        }),
                                    }}
                                    value={formData?.property}
                                    onChange={(selectedOption) => handleDropDownChange({ name: 'property', value: selectedOption })}
                                    options={propertyList?.map(country => ({ value: country, label: country?.text }))}
                                />
                            </div>
                            <div className="d-flex">
                                <label htmlFor="validationCustom18" className="form-label">Unit No</label>
                                <Select
                                    className="p-0 w-100"
                                    theme={(theme) => ({
                                        ...theme,
                                        borderRadius: 5,
                                        padding: 9,
                                    })}
                                    menuPortalTarget={document.body}
                                    styles={{
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                        option: (provided, state) => ({
                                            ...provided,
                                            backgroundColor: state.isFocused ? "#F5F5F5 " : null,
                                            color: state.isFocused ? "#000 " : "#000",
                                            ":hover": {
                                                backgroundColor: "#F5F5F5",
                                                color: "#000 ",
                                            },
                                        }),
                                    }}
                                    value={formData?.unitNo}
                                    onChange={(selectedOption) => handleDropDownChange({ name: 'unitNo', value: selectedOption })}
                                    options={unitList?.map(country => ({ value: country, label: country?.text }))}
                                />
                            </div>
                            <div className="d-flex">
                                <label className="form-label w-0">From Date</label>
                                <DatePicker
                                    selected={formData.from}
                                    onChange={(date) => handleDateChange({ name: 'from', value: date })}
                                    dateFormat="dd-MMM-yyyy"
                                    className="p-2 form-control w-100"
                                />
                            </div>
                            <div className="d-flex">
                                <label className="form-label w-0">To Date</label>
                                <DatePicker
                                    selected={formData.to}
                                    onChange={(date) => handleDateChange({ name: 'to', value: date })}
                                    dateFormat="dd-MMM-yyyy"
                                    className="p-2 form-control w-100"
                                />
                            </div>
                            <div className="d-flex gap-1">
                                {loader === true ?
                                    <div className='mainbtnGry flex-end gap-2 w-50'>
                                        <ClipLoader color="#ffffff" className='mt-1' size={25} />
                                    </div>
                                    :
                                    <div onClick={() => getItems(1)} className="mainbtnGry flex-end gap-2 w-50">
                                        <FaFileCircleExclamation color="#fff" className="center mt-2 mx-2" />
                                        <div className="center">Search</div>
                                    </div>
                                }
                                <div onClick={() => handleClearFilters()} className="mainbtnRed flex-end gap-2 w-50">
                                    <RiRefreshLine color="#fff" className="center mt-2 mx-2" />
                                    <div className="center">Reset</div>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
            <div className="border-box2 p-3">
                {loading ? (
                    <div>Loading...</div>
                ) : (
                    <>
                        <div className="d-flex justify-contant-between ">
                            <h5 className="col mainInvHead">Unit Vacant Report</h5>
                            <button className="btn btn-primary" onClick={exportPDF}>
                                Export as PDF
                            </button>
                            <button className="btn btn-secondary mx-3" onClick={exportExcel}>
                                Export as Excel
                            </button>
                        </div>
                        <div className="gridshad  mt-3">
                            <div className="ag-theme-quartz" style={{ height: 650 }}>
                                <AgGridReact
                                    getRowStyle={(params) => {
                                        if (params.node.rowIndex % 2 === 0) {
                                            return { background: "#F0EDF5" };
                                        } else {
                                            return { background: "#fff" };
                                        }
                                    }}
                                    rowData={rowData}
                                    enableFilter={true}
                                    floatingFilter={true}
                                    columnDefs={columnDefs}
                                />
                            </div>
                            <Pagination
                                pageSize={pageSize}
                                setPageSize={setPageSize}
                                pagination={pagination}
                                getItems={getItems}
                            />
                        </div>
                    </>
                )}
            </div>
        </div >
    );
};
export default UnitVacantReport;
